import { useState, useCallback } from 'react';
import { reportService } from '@services';
import { ReportWidget } from '@app-types/visualization';

export const useWidgetExecution = () => {
  const [executingWidgets, setExecutingWidgets] = useState<Set<number>>(new Set());
  const [widgetData, setWidgetData] = useState<Map<number, Record<string, unknown>[]>>(new Map());

  // Execute widget by ID - SQL is securely retrieved from database on backend
  const executeWidget = useCallback(async (widget: ReportWidget) => {
    console.log('🔍 Executing widget:', widget.name, 'ID:', widget.id, 'Type:', widget.visualization_type);
    setExecutingWidgets((prev) => new Set(prev).add(widget.id));

    try {
      // Security: Only send widget_id - SQL is retrieved from DB on backend
      const result = await reportService.executeWidgetSQL(widget.id);

      console.log('✅ Widget data received:', widget.name);

      // Store the data
      setWidgetData((prev) => new Map(prev).set(widget.id, result.results));

      console.log(
        '📊 Widget data set for ID:',
        widget.id,
        'Chart Type:',
        widget.visualization_type,
        'Data count:',
        result.results?.length
      );
      return result;
    } catch (error) {
      console.error('❌ Error executing widget:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to execute widget "${widget.name}": ${errorMessage}`);
    } finally {
      setExecutingWidgets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(widget.id);
        return newSet;
      });
    }
  }, []);

  // Execute multiple widgets
  const executeWidgets = useCallback(
    async (widgets: ReportWidget[]) => {
      console.log('🚀 Auto-loading data for', widgets.length, 'widgets');

      // Execute all widgets in parallel
      const executions = widgets.map((widget) => {
        console.log('📊 Auto-executing widget:', widget.name, 'Type:', widget.visualization_type);
        return executeWidget(widget).catch((error) => {
          console.error(`Failed to execute widget ${widget.name}:`, error);
          return null;
        });
      });

      await Promise.allSettled(executions);
    },
    [executeWidget]
  );

  // Get widget data
  const getWidgetData = useCallback(
    (widgetId: number) => {
      return widgetData.get(widgetId);
    },
    [widgetData]
  );

  // Check if widget is executing
  const isWidgetExecuting = useCallback(
    (widgetId: number) => {
      return executingWidgets.has(widgetId);
    },
    [executingWidgets]
  );

  // Clear widget data
  const clearWidgetData = useCallback((widgetId: number) => {
    setWidgetData((prev) => {
      const newMap = new Map(prev);
      newMap.delete(widgetId);
      return newMap;
    });
  }, []);

  return {
    executeWidget,
    executeWidgets,
    getWidgetData,
    isWidgetExecuting,
    clearWidgetData,
    executingWidgets,
    widgetData
  };
};
